// frontend/src/App.js

import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './context/AuthContext';
import { PermissionsProvider } from './context/PermissionsContext';
import PermissionGuard from './components/Auth/PermissionGuard';
import ForbiddenPage from './components/Auth/ForbiddenPage';
import { TranslationProvider } from './context/TranslationContext';
import { useTranslation } from './context/TranslationContext';
import { applyTheme, getSavedTheme } from './config/theme';
import ProtectedRoute from './components/Auth/ProtectedRoute';
import Login from './components/Auth/Login';
import Layout from './components/Layout/Layout';
import OrderList from './components/Texpact/OrderList';
import QCRLookup from './components/Texpact/QCRLookup';
import { OrderDetailsPage } from './components/Texpact/OrderDetails';
import ShipmentList from './components/Texpact/ShipmentList';
import MessageList from './components/Texpact/MessageList';
import SettingsRouter from './components/Settings/SettingsRouter';
import Divisions from './components/Samples/Divisions';
import ProductionTypes from './components/Samples/ProductionTypes';
import SampleOrderTypes from './components/Samples/SampleOrderTypes';
import Customers from './components/Samples/Customers';
import Collections from './components/Samples/Collections';
import SizeTables from './components/Samples/SizeTables';
import NumberSeries from './components/Samples/NumberSeries';
import SampleOrderList from './components/Samples/SampleOrderList';
import SampleOrderForm from './components/Samples/SampleOrderForm';
import CuttingPlanList from './components/CuttingPlan/CuttingPlanList';
import CuttingPlanForm from './components/CuttingPlan/CuttingPlanForm';
import SampleOrderArticleList from './components/Samples/SampleOrderArticleList';
import ProductList from './components/Products/ProductsList';
import ProductForm from './components/Products/ProductForm';

import MeasurementList from './components/Quality/MeasurementList';
import MeasurementEntry from './components/Quality/MeasurementEntry';
import AreasSettings from './components/Settings/AreasSettings';
import MyTasksList from './components/Samples/MyTasksList';

import ActionPlanList from './components/ActionPlans/ActionPlanList';
import ActionPlanTasksList from './components/ActionPlans/ActionPlanTasksList';
import ActionPlanDetail from './components/ActionPlans/ActionPlanDetail';
import APLookupsSettings from './components/ActionPlans/APLookupsSettings';
import APAreas from './components/ActionPlans/Areas';
import APSubAreas from './components/ActionPlans/SubAreas';
import APCategories from './components/ActionPlans/Categories';

import SystemConfigAdmin from './components/Settings/SystemConfigAdmin';
import NotificationEntityFieldsAdmin from './components/Settings/NotificationEntityFieldsAdmin';

import SampleOrderCalendar from './components/Samples/SampleOrderCalendar';

import MyTasksCalendar from './components/Samples/MyTasksCalendar';

import ResetPassword from './components/Auth/ResetPassword';

import ImportSetupFios from './components/FastReact/ImportSetupFios';
import SetupFiosNavision from './components/FastReact/SetupFiosNavision';
import PlanoTeares from './components/FastReact/PlanoTeares';
import PrepPickup from './components/FastReact/PrepPickup';
import Restock from './components/FastReact/Restock';
import RestockGroups from './components/FastReact/RestockGroups';
import DailyRequirements from './components/FastReact/DailyRequirements';


import PowerBISubscriptions from './components/PowerBI/PowerBISubscriptions';

import MobileRoute from './components/Mobile/MobileRoute';
import MobileLogin from './components/Mobile/MobileLogin';
import MobileHome from './components/Mobile/MobileHome';
import MobilePlanoTeares from './components/Mobile/FastReact/MobilePlanoTeares';
import MobilePrepDetail from './components/Mobile/FastReact/MobilePrepDetail';
import MobilePickup from './components/Mobile/FastReact/MobilePickup';
import MobileRestock from './components/Mobile/FastReact/MobileRestock';
import MobileRestockGroups from './components/Mobile/FastReact/MobileRestockGroups';


import EdiClientConfig from './components/Edi/EdiClientConfig';
import EdiMessageLog from './components/Edi/EdiMessageLog';

import ExportErpPage from './components/QuantixGo/ExportErpPage';
import DeviceRequestsPage from './components/QuantixGo/DeviceRequestsPage';
import DeviceManagementPage from './components/QuantixGo/DeviceManagementPage';
import TransportCostsPage from './components/QuantixGo/TransportCostsPage';

import { texpactAPI } from './api/client';
import './styles/GlobalStyles.css';
import './App.css';

// Dashboard Component
const Dashboard = () => {
  const { t } = useTranslation();

  return (
    <div className="page-container">
      <div className="page-header">
        <h1>{t('dashboard.welcome_title', 'Welcome 👋')}</h1>
        <p className="page-subtitle">
          {t(
            'dashboard.welcome_subtitle',
            'Welcome to your Production Management Portal'
          )}
        </p>
      </div>

      <div
        style={{
          textAlign: 'center',
          padding: '80px 20px',
          color: 'var(--gray-600)',
        }}
      >
        <div style={{ fontSize: '64px', marginBottom: '24px' }}>🚀</div>

        <h2 style={{ marginBottom: '16px', color: 'var(--gray-900)' }}>
          {t('dashboard.get_started', 'Ready to get started?')}
        </h2>

        <p style={{ fontSize: '16px', maxWidth: '600px', margin: '0 auto 32px' }}>
          {t(
            'dashboard.description',
            'Use the sidebar to navigate through orders, shipments, and settings. Your operational data will appear here once available.'
          )}
        </p>

        <a href="/samples/orders" className="btn btn-primary">
          📦 {t('dashboard.view_orders', 'Pedidos de Amostra')}
        </a>
      </div>
    </div>
  );
};

function App() {
  useEffect(() => {
    const savedTheme = getSavedTheme();
    applyTheme(savedTheme);
  }, []);

  return (
    <AuthProvider>
      <PermissionsProvider>
        <TranslationProvider>
          <Router>
            <Routes>
              {/* Public routes */}
              <Route path="/login" element={<Login />} />
              <Route path="/reset-password" element={<ResetPassword />} />

              {/* Mobile routes — own layout, own auth check */}
                          
              <Route path="/mobile/login" element={<MobileLogin />} />
              <Route path="/mobile" element={<MobileRoute><MobileHome /></MobileRoute>} />
              <Route path="/mobile/plano-teares" element={
                <MobileRoute menuKey="mobile_fastreact_plano_teares"><MobilePlanoTeares /></MobileRoute>
              } />
              <Route path="/mobile/plano-teares/:poSetupId" element={
                <MobileRoute menuKey="mobile_fastreact_plano_teares"><MobilePrepDetail /></MobileRoute>
              } />
              <Route path="/mobile/pickup" element={
                <MobileRoute menuKey="mobile_fastreact_pickup"><MobilePickup /></MobileRoute>
              } />
              <Route path="/mobile/restock" element={
                <MobileRoute menuKey="mobile_fastreact_restock"><MobileRestock /></MobileRoute>
              } />
              <Route path="/mobile/restock-groups" element={
                <MobileRoute menuKey="mobile_fastreact_restock_groups"><MobileRestockGroups /></MobileRoute>
              } />



              {/* Protected routes */}
              <Route
                path="/*"
                element={
                  <ProtectedRoute>
                    <Layout>
                      <Routes>
                        <Route path="/" element={<Navigate to="/dashboard" replace />} />
                        <Route path="/dashboard" element={<Dashboard />} />

                        {/* Orders routes */}
                        <Route path="/orders" element={<OrderList />} />
                        <Route path="/orders/view/:publicId" element={<OrderDetailsPage />} />

                        <Route path="/shipments" element={<ShipmentList />} />
                        <Route path="/messages" element={<MessageList />} />
                        <Route path="/qcr" element={<QCRLookup />} />

                        <Route path="/settings/*" element={<SettingsRouter />} />
                        <Route path="/forbidden" element={<ForbiddenPage />} />
                        <Route path="/settings/areas" element={<AreasSettings />} />

                        {/* Samples Routes */}
                        <Route path="/samples/divisions" element={<Divisions />} />
                        <Route path="/samples/production-types" element={<ProductionTypes />} />
                        <Route path="/samples/order-types" element={<SampleOrderTypes />} />
                        <Route path="/samples/customers" element={<Customers />} />
                        <Route path="/samples/collections" element={<Collections />} />
                        <Route path="/samples/size-tables" element={<SizeTables />} />
                        <Route path="/samples/number-series" element={<NumberSeries />} />
                        <Route
                          path="/samples/orders"
                          element={
                            <PermissionGuard menuKey="sample_orders" operation="read">
                              <SampleOrderList />
                            </PermissionGuard>
                          }
                        />

                        <Route path="/fastreact/setup-fios" element={<ImportSetupFios />} />
                        <Route path="/fastreact/setup-fios-navision" element={<SetupFiosNavision />} />
                        <Route path="/fastreact/plano-teares" element={<PlanoTeares />} />
                        <Route path="/fastreact/prep-pickup" element={<PrepPickup />} />
                        <Route path="/fastreact/restock" element={<Restock />} />

                        <Route path="/fastreact/restock-groups" element={
                          <PermissionGuard menuKey="fastreact_restock_groups" operation="read">
                            <RestockGroups />
                          </PermissionGuard>
                        } />
                        <Route path="/fastreact/daily-requirements" element={
                          <PermissionGuard menuKey="fastreact_daily_requirements" operation="read">
                            <DailyRequirements />
                          </PermissionGuard>
                        } />                        


                        <Route path="/powerbi/subscriptions" element={<PowerBISubscriptions />} />

                        <Route path="/samples/calendar" element={<SampleOrderCalendar />} />
                        <Route path="/samples/my-tasks/calendar" element={<MyTasksCalendar />} />

                        <Route path="/samples/orders/new" element={<SampleOrderForm />} />
                        <Route path="/samples/orders/:sampleOrderId" element={<SampleOrderForm />} />
                        <Route path="/sample-order-articles" element={<SampleOrderArticleList />} />
                        <Route path="/samples/my-tasks" element={<MyTasksList />} />

                        <Route
                          path="/products"
                          element={
                            <PermissionGuard menuKey="products" operation="read">
                              <ProductList />
                            </PermissionGuard>
                          }
                        />
                        <Route path="/products/new" element={<ProductForm />} />
                        <Route path="/products/:productId" element={<ProductForm />} />

                        {/* EDI */}
                        <Route
                          path="/edi/clientes"
                          element={
                            <PermissionGuard menuKey="edi_client_config" operation="read">
                              <EdiClientConfig />
                            </PermissionGuard>
                          }
                        />
                        <Route
                          path="/edi/mensagens"
                          element={
                            <PermissionGuard menuKey="edi_messages" operation="read">
                              <EdiMessageLog />
                            </PermissionGuard>
                          }
                        />

                        {/* QuantixGo */}
                        <Route
                          path="/quantixgo/export-erp"
                          element={
                            <PermissionGuard menuKey="quantixgo_export_erp" operation="read">
                              <ExportErpPage />
                            </PermissionGuard>
                          }
                        />
                        <Route
                          path="/quantixgo/device-requests"
                          element={
                            <PermissionGuard menuKey="quantixgo_device-requests" operation="read">
                              <DeviceRequestsPage />
                            </PermissionGuard>
                          }
                        />

                        <Route
                          path="/quantixgo/devices"
                          element={
                            <PermissionGuard menuKey="quantixgo_devices" operation="read">
                              <DeviceManagementPage />
                            </PermissionGuard>
                          }
                        />

                        <Route path="/quantixgo/transport-costs" element={
                          <PermissionGuard menuKey="quantixgo_transport_costs" operation="read">
                            <TransportCostsPage />
                          </PermissionGuard>
                        } />


                        {/* Cutting Plans Routes */}
                        <Route path="/cutting-plans" element={<CuttingPlanList />} />
                        <Route path="/cutting-plans/new" element={<CuttingPlanForm />} />
                        <Route path="/cutting-plans/:planId" element={<CuttingPlanForm />} />

                        {/* Quality Routes */}
                        <Route path="/quality/measurements" element={<MeasurementList />} />
                        <Route path="/quality/measurements/:headerPublicId" element={<MeasurementEntry />} />

                        {/* Action Plans — specific paths MUST come before /:publicId */}
                        <Route
                          path="/action-plans"
                          element={
                            <PermissionGuard menuKey="action_plans" operation="read">
                              <ActionPlanList />
                            </PermissionGuard>
                          }
                        />
                        <Route
                          path="/action-plans/new"
                          element={
                            <PermissionGuard menuKey="action_plans" operation="create">
                              <ActionPlanDetail />
                            </PermissionGuard>
                          }
                        />

                        <Route path="/action-plans/tasks" element={<ActionPlanTasksList />} />

                        <Route
                          path="/action-plans/areas"
                          element={
                            <PermissionGuard menuKey="ap_areas" operation="read">
                              <APAreas />
                            </PermissionGuard>
                          }
                        />
                        <Route
                          path="/action-plans/sub-areas"
                          element={
                            <PermissionGuard menuKey="ap_sub_areas" operation="read">
                              <APSubAreas />
                            </PermissionGuard>
                          }
                        />
                        <Route
                          path="/action-plans/categories"
                          element={
                            <PermissionGuard menuKey="ap_categories" operation="read">
                              <APCategories />
                            </PermissionGuard>
                          }
                        />
                        <Route
                          path="/action-plans/settings"
                          element={
                            <PermissionGuard menuKey="ap_settings" operation="read">
                              <APLookupsSettings />
                            </PermissionGuard>
                          }
                        />
                        <Route
                          path="/action-plans/:publicId"
                          element={
                            <PermissionGuard menuKey="action_plans" operation="read">
                              <ActionPlanDetail />
                            </PermissionGuard>
                          }
                        />

                        <Route
                          path="/settings/system-config"
                          element={
                            <PermissionGuard menuKey="system_config" operation="write">
                              <SystemConfigAdmin />
                            </PermissionGuard>
                          }
                        />

                        <Route
                          path="/settings/notification-fields"
                          element={
                            <PermissionGuard menuKey="notifications" operation="write">
                              <NotificationEntityFieldsAdmin />
                            </PermissionGuard>
                          }
                        />

                        <Route path="*" element={<Navigate to="/dashboard" replace />} />
                      </Routes>
                    </Layout>
                  </ProtectedRoute>
                }
              />
            </Routes>
          </Router>
        </TranslationProvider>
      </PermissionsProvider>
    </AuthProvider>
  );
}

export default App;